// components/onboarding/RegisterStep1.tsx
"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { FcGoogle } from "react-icons/fc";
import { Role } from "@/lib/types";

type Step1Props = {
  onNext: (data: {
    intent: string;
    flowType: "consultation" | "account";
  }) => void;
};

const intentOptions = [
  {
    id: "unsure",
    label: "I'm not sure",
    desc: "Pick this if you're unsure about your solar needs",
    icon: "/assets/icons/emoji-confused-light.svg",
    active: "/assets/icons/emoji-confused.svg",
  },
  {
    id: "new",
    label: "I'm new",
    desc: "Create an account to explore other features",
    icon: "/assets/icons/emoji-new-light.svg",
    active: "/assets/icons/emoji-new.svg",
  },
];

export default function RegisterStep1({ onNext }: Step1Props) {
  const [selected, setSelected] = useState<string | null>(null);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role>("HOMEOWNER");

  const handleContinue = () => {
    if (!selected) return;

    const flowType = selected === "unsure" ? "consultation" : "account";
    onNext({ intent: selected, flowType });
  };

  const handleGoogleSignup = () => {
    if (!selectedRole) {
      toast.error("Please select your role first");
      return;
    }
    // Redirect to Google OAuth with selected role
    window.location.href = `${process.env.NEXT_PUBLIC_BACKEND_URL}/auth/google/login`;
  };

  return (
    <div className="flex h-full flex-col justify-center">
      {/* Title & Subtitle */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8 text-center"
      >
        <h1 className="text-3xl font-semibold text-gray-800">
          What are you here for?
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          Let's find your perfect solar solution, fast and easy
        </p>
      </motion.div>

      {/* Google Signup Section */}
      <div className="mb-6 h-fit">
        {/* Google Signup Button */}
        <Button
          type="button"
          variant="outline"
          className="flex w-full items-center justify-center gap-3 border-2"
          onClick={handleGoogleSignup}
        >
          <FcGoogle className="h-6 w-6" />
          Continue with Google
        </Button>

        <p className="mt-2 text-center text-xs text-gray-500">
          First time? We'll ask for your role once
        </p>
      </div>

      {/* Divider */}
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white px-2 text-gray-500">or</span>
        </div>
      </div>

      {/* Option Cards */}
      <div className="flex flex-col space-y-4 md:flex-row md:space-x-6 md:space-y-0">
        {intentOptions.map((option) => (
          <motion.button
            key={option.id}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelected(option.id)}
            className={`relative flex w-full flex-col items-center justify-between rounded-xl border-2 p-5 transition-all ${selected === option.id
                ? "border-green-700 bg-white"
                : "border-gray-200 bg-white hover:border-gray-300"
              }`}
          >
            <div className="mb-4 flex items-center justify-center">
              <Image
                src={selected === option.id ? option.active : option.icon}
                width={80}
                height={80}
                alt={option.label}
                className="h-11 w-11"
              />
            </div>
            <span className="text-lg font-medium text-gray-800">
              {option.label}
            </span>
            <p className="mt-2 text-center text-xs text-gray-600">
              {option.desc}
            </p>
            {/* Checkmark indicator */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4, duration: 0.3 }}
              className={`absolute right-4 top-4 z-20 flex h-6 w-6 items-center justify-center rounded-full ${selected === option.id
                  ? "bg-green-600"
                  : "border-2 border-gray-300"
                }`}
            >
              {selected === option.id && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 6L9 17l-4-4" />
                </svg>
              )}
            </motion.div>
          </motion.button>
        ))}
      </div>

      {/* Login Link & Continue Button */}
      <div className="mt-10 flex flex-col items-center justify-between md:flex-row">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="text-sm text-gray-700"
        >
          Already have an account?{" "}
          <Link
            href="/auth/login"
            className="font-medium text-green-700 hover:underline"
          >
            Log in here
          </Link>
        </motion.p>
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          whileTap={{ scale: 0.95 }}
          disabled={!selected}
          onClick={handleContinue}
          className={`rounded-full px-6 py-3 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${selected
              ? "bg-green-800 text-white hover:bg-green-700"
              : "cursor-not-allowed bg-gray-300 text-gray-500"
            }`}
        >
          Continue
        </motion.button>
      </div>
    </div>
  );
}
